import { useApiQuery } from "@gladis/api-client";
import { CardForm, CardSet, useExpenses, type CardSummaryDto } from "@gladis/domain-expenses/ui";
import type { CurrentUserDto, MemberDto } from "@gladis/shared";
import { Button } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { Page } from "../../../components/page.js";

export const Route = createFileRoute("/_authenticated/_shell/cards")({
  component: Cards,
});

/**
 * Cards — where the household's payment sources are made and managed.
 *
 * ==========================================================================================
 * ITS OWN PAGE, AND NOT A SECTION OF EXPENSES
 * ==========================================================================================
 *
 * Cards started as a section on the Expenses page, which is where the settle-up needs them. Two
 * things were wrong with that. A card is set up ONCE and then read for years, while an expense is
 * entered every month — so the two belong to different rhythms, and putting the setup task on the
 * page you open monthly makes the monthly page longer for no monthly benefit. And the Expenses page
 * already carries three sections that answer three different questions; the cards were a fourth,
 * about a different noun.
 *
 * The Expenses page keeps everything it needs: the card picker on the form, the card on each row,
 * and the settle-up derived from card ownership. What it no longer carries is the SETUP.
 *
 * ==========================================================================================
 * WHY IT READS `/api/expenses`
 * ==========================================================================================
 *
 * A card's monthly total is the sum of the active expenses bound to it (FR-046), so any endpoint
 * that returned cards WITH their totals would have to read the expenses anyway. Rather than a second
 * endpoint computing the same figure a second way — which is how two pages come to disagree about
 * one card — this page reads the same payload the Expenses page reads, and TanStack Query serves
 * both from one cache entry when a member moves between them.
 *
 * The cost is honest and worth stating: the payload also carries the rows, the totals and the
 * mortgage-interest section, none of which this page renders. If a future feature makes that read
 * heavier, `GET /api/cards` with its own totals query is the answer — and it is not the answer today,
 * because it would duplicate the arithmetic before it saved anything.
 */
function Cards(): ReactNode {
  const t = useT();
  const [adding, setAdding] = useState(false);
  /*
   * WHICH card the form is editing, or null for a new one — the same shape the Expenses page holds for a row.
   *
   * The card itself rather than an id: the form fills its fields and its live preview from it, and holding an
   * id would mean looking the card up again in a payload that may have refetched in between.
   */
  const [editing, setEditing] = useState<CardSummaryDto | null>(null);

  // Served from the cache `_authenticated` already populated — no extra request.
  const me = useApiQuery<CurrentUserDto>(["me"], "/me");
  const householdId = me.data?.activeHousehold?.id;

  const page = useExpenses();
  const cards = page.data?.cards ?? [];

  /*
   * The member list, for the owner picker on the form.
   *
   * The server never trusts it: `createCard` is handed identity's answer by the composition root and
   * validates the owner against that. This copy exists so the form can OFFER the right names, and
   * offering a wrong one is refused rather than accepted.
   */
  const members = useApiQuery<{ members: MemberDto[] }>(
    ["members", householdId ?? "none"],
    `/households/${householdId}/members`,
    { enabled: householdId !== undefined },
  );

  return (
    <Page>
      {/*
       * ADDING A CARD IS THIS PAGE'S PRIMARY ACTION, so it goes in the one place a primary action
       * goes: the end of the title row. It used to live in `CardSet`'s own header, which put the
       * page's most important control at whatever height that section happened to start — below a
       * form, once the form was open.
       *
       * `variant="primary"` is the exception `Button` makes you ask for by name, and it is earned
       * here: on a page whose only other controls are per-card archive links, this is the thing a
       * member came to do.
       */}
      <Page.Title
        actions={
          <Button
            variant="primary"
            // Opens, and no longer toggles: the dialog has three ways to close and this control is not
            // one of them — while it is open, this button is behind the modal and inert. `aria-haspopup`
            // rather than the `aria-expanded` a toggle would carry.
            aria-haspopup="dialog"
            onClick={() => {
              setAdding(true);
            }}
          >
            {t("exp.card.addHeading")}
          </Button>
        }
      >
        {t("nav.cards")}
      </Page.Title>
      <Page.Body>
        <div className="col-span-full flex flex-col gap-6">
          {/*
            RENDERED UNCONDITIONALLY, and `open` is what decides whether it is seen.

            The `{adding ? <CardForm/> : null}` this replaces would mount the form fresh on every open,
            which a native `<dialog>` cannot work with: `Dialog` calls `showModal()` from an effect on
            the element it already has a ref to, and an element that appears in the same commit as the
            `open` prop has no first render in which to be closed. Keeping it mounted also means the
            member's half-typed name survives an accidental Escape, since the state stays put.
          */}
          <CardForm
            open={adding || editing !== null}
            onClose={() => {
              setAdding(false);
              setEditing(null);
            }}
            editing={editing}
            members={members.data?.members ?? []}
            onCreated={() => {
              setAdding(false);
              setEditing(null);
            }}
          />

          <CardSet
            cards={cards}
            members={members.data?.members ?? []}
            onEdit={(card) => {
              setEditing(card);
            }}
          />
        </div>
      </Page.Body>
    </Page>
  );
}
